import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import {
  getPositionInitMargin,
  getPositionUnrealizedPnL,
  getPositionValue,
} from '@/features/account/utils/math'; // eslint-disable-line import/no-restricted-paths
import { privateV2Api } from '@/lib/api-clients/rest-client';
import { mergeLeverages } from '@/lib/leverage-db';
import { getDefaultBigDecimal } from '@/lib/utils';
import {
  Position,
  Positions,
  useAccountStore,
} from '@/store/use-account-store';
import {
  COLLATERAL_DECIMALS,
  getMarketStore,
  setAllLeverages,
} from '@/store/use-markets-store';
import { NewOrder } from '@/store/use-orders-store';
import { OrderStatus, OrderType } from '@/types';
import { handleOrderForStore } from '@/utils/handle-store-data';
import { parseDecimalToBigInt } from '@/utils/value-format';

import { getEstFillPrice, getFee } from '../utils/math';

type Params = {
  offset?: number;
  limit?: number;
};

type PositionsResponse = {
  positions: PositionV2[];
  total: number;
};

export const getPositionsQueryKey = (
  apiKey: string | undefined,
  params?: Params | undefined,
) => ['/positions/opened', apiKey, params];

export default function useOpenPositions(params: Params = {}) {
  const authParams = useAccountStore(
    useShallow((state) => {
      const authParams = state.getAuthParams(state.address);
      if (!authParams) return;
      return {
        apiKey: authParams.apiKey,
        accGroup: authParams.accountGroup,
      };
    }),
  );
  return useQuery({
    enabled: authParams?.accGroup === 2 && !!authParams?.apiKey,
    queryKey: getPositionsQueryKey(authParams?.apiKey, params),
    staleTime: 0,
    queryFn: () =>
      privateV2Api
        .get<PositionsResponse>('/v2/positions/opened', {
          params,
        })
        .then((res) => {
          const positions: Positions = res.positions.reduce(
            (prev, { symbol, ...pos }) => {
              const { marketData, book, marketSpec } =
                getMarketStore(symbol).getState();
              const entryPrice = parseDecimalToBigInt(
                pos.openPrice,
                marketSpec.priceDecimals,
              );
              const size = parseDecimalToBigInt(
                pos.quantity,
                marketSpec.sizeDecimals,
              );
              const cost = entryPrice * size;
              const isLong = pos.side === 'long';

              const funding = parseDecimalToBigInt(
                pos.funding,
                COLLATERAL_DECIMALS,
              );

              const leverage = parseDecimalToBigInt(pos.leverage, 2n);
              const initMarginRatio = 10n ** 6n / leverage;
              const initMargin = getPositionInitMargin(
                symbol,
                cost,
                initMarginRatio,
              ).bigint;

              const fee = getFee(symbol, size, marketData.markPrice);

              const value = getPositionValue(symbol, size);
              const estFillPrice =
                getEstFillPrice(!isLong, size, book, marketSpec.priceDecimals)
                  ?.bigint ?? marketData.markPrice;

              const unrealizedPnl = getPositionUnrealizedPnL(
                symbol,
                estFillPrice,
                {
                  isLong,
                  size,
                  cost,
                  fee,
                  funding,
                  initMargin,
                },
              );

              const position: Position = {
                id: pos.positionId,
                isLong,
                size,
                cost,
                initMarginRatio,
                unrealizedPnl,
                value,
                fee,
                funding,
                initMargin,
                liqPrice: getDefaultBigDecimal(marketSpec.priceDecimals),
              };

              return { ...prev, [symbol]: position };
            },
            {} as Record<string, Position>,
          );

          const positionLeverages: Record<string, number> = {};
          for (const { symbol, leverage: lev } of res.positions) {
            positionLeverages[symbol] = Number(lev);
          }
          setAllLeverages(positionLeverages);

          const { address } = useAccountStore.getState();
          if (address) {
            mergeLeverages(address, positionLeverages);
          }

          const { setAccount } = useAccountStore.getState();
          // not setting time prevents blocking /account
          // time set here would be greater than returned from /account response
          setAccount({ positions, hasHydrated: true });
          useAccountStore.getState().updateAccount();

          // update TP/SLs after positions to have up-to-date data
          res.positions.forEach(
            ({ symbol, side, leverage, takeProfits, stopLosses }) => {
              const initMarginRatio =
                10n ** 6n / parseDecimalToBigInt(leverage, 2n);
              const isLong = side === 'long';
              const { marketSpec } = getMarketStore(symbol).getState();
              takeProfits?.forEach((tp) => {
                const order: NewOrder = {
                  id: tp.id,
                  initMarginRatio,
                  isBuy: !isLong,
                  limitPrice: parseDecimalToBigInt(
                    tp.triggerPrice,
                    marketSpec.priceDecimals,
                  ),
                  size: parseDecimalToBigInt(
                    tp.quantity,
                    marketSpec.sizeDecimals,
                  ),
                  postTime: tp.updatedAt,
                  reduceOnly: true,
                  symbol,
                  orderType: OrderType.TAKE_PROFIT,
                  status: OrderStatus.NEW,
                };
                handleOrderForStore(order, { skipFill: true });
              });
              stopLosses?.forEach((sl) => {
                const order: NewOrder = {
                  id: sl.id,
                  initMarginRatio,
                  isBuy: !isLong,
                  limitPrice: parseDecimalToBigInt(
                    sl.triggerPrice,
                    marketSpec.priceDecimals,
                  ),
                  size: parseDecimalToBigInt(
                    sl.quantity,
                    marketSpec.sizeDecimals,
                  ),
                  postTime: sl.updatedAt,
                  reduceOnly: true,
                  symbol,
                  orderType: OrderType.STOP_LOSS,
                  status: OrderStatus.NEW,
                };
                handleOrderForStore(order, { skipFill: true });
              });
            },
          );

          return res;
        }),
  });
}
